"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const webapi_1 = require("@fegobull/webapi");
const vue_demi_1 = require("vue-demi");
const utils_1 = require("../../common/utils");
const { useTeleport } = webapi_1.use;
useTeleport(document.body);
exports.default = (0, vue_demi_1.defineComponent)({
    name: 'DatePickerPopup',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        value: {
            type: String,
            default: ''
        },
        availableDates: {
            type: Array,
            required: true
        },
        dateStyle: {
            type: String,
            default: 'monthFirst'
        }
    },
    emits: ['update:visible', 'confirm', 'cancel'],
    setup(props, { emit }) {
        const monthColumnRef = (0, vue_demi_1.ref)(null);
        const dayColumnRef = (0, vue_demi_1.ref)(null);
        const monthItemRefs = (0, vue_demi_1.ref)(new Map());
        const dayItemRefs = (0, vue_demi_1.ref)(new Map());
        // 临时选中的值（用于确认前预览）
        const tempValue = (0, vue_demi_1.ref)(props.value);
        // 选中的月份
        const selectedMonth = (0, vue_demi_1.ref)('');
        // 月份列表
        const monthList = (0, vue_demi_1.computed)(() => {
            return (0, utils_1.extractMonths)(props.availableDates);
        });
        // 根据选中月份过滤的日期列表
        const filteredDates = (0, vue_demi_1.computed)(() => {
            if (!selectedMonth.value) {
                return [];
            }
            return (0, utils_1.filterDatesByMonth)(props.availableDates, selectedMonth.value);
        });
        // 多语言相关
        const lang = (0, utils_1.getCurrentLang)();
        // 判断是否为英文（用于调整列顺序）
        const isEnglishLang = (0, utils_1.isEnglish)();
        const cancelText = (0, vue_demi_1.computed)(() => {
            const texts = {
                0: '取消', // CN
                1: '取消', // HK
                2: 'Cancel', // EN
                5: 'キャンセル' // JA
            };
            return texts[lang] || texts[0];
        });
        const confirmText = (0, vue_demi_1.computed)(() => {
            const texts = {
                0: '确认', // CN
                1: '確認', // HK
                2: 'Confirm', // EN
                5: '確認' // JA
            };
            return texts[lang] || texts[0];
        });
        // 设置月份项引用
        const setMonthItemRef = (el, month) => {
            if (el) {
                monthItemRefs.value.set(month, el);
            }
        };
        // 设置日期项引用
        const setDayItemRef = (el, date) => {
            if (el) {
                dayItemRefs.value.set(date, el);
            }
        };
        // 判断日期是否禁用（可以根据业务逻辑扩展）
        const isDateDisabled = (dateSlot) => {
            // 如果该日期没有可用时段，则禁用
            return !dateSlot.timeRanges || dateSlot.timeRanges.length === 0;
        };
        // 处理月份选择
        const handleMonthSelect = (month) => {
            selectedMonth.value = month;
            // 选中月份后，自动滚动到第一个可用日期
            (0, vue_demi_1.nextTick)(() => {
                scrollToFirstAvailableDay();
            });
        };
        // 处理日期选择
        const handleDaySelect = (dateSlot) => {
            if (isDateDisabled(dateSlot)) {
                return;
            }
            tempValue.value = dateSlot.date;
        };
        // 滚动到第一个可用日期
        const scrollToFirstAvailableDay = () => {
            if (!dayColumnRef.value || filteredDates.value.length === 0) {
                return;
            }
            // 找到第一个非禁用的日期
            const firstAvailableDate = filteredDates.value.find((dateSlot) => !isDateDisabled(dateSlot));
            if (!firstAvailableDate) {
                return;
            }
            const dayItem = dayItemRefs.value.get(firstAvailableDate.date);
            if (dayItem && dayColumnRef.value) {
                dayItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };
        // 滚动到选中的月份
        const scrollToSelectedMonth = () => {
            if (!monthColumnRef.value || !selectedMonth.value) {
                return;
            }
            const monthItem = monthItemRefs.value.get(selectedMonth.value);
            if (monthItem && monthColumnRef.value) {
                monthItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };
        // 滚动到选中的日期
        const scrollToSelectedDay = () => {
            if (!dayColumnRef.value || !tempValue.value) {
                return;
            }
            const dayItem = dayItemRefs.value.get(tempValue.value);
            if (dayItem && dayColumnRef.value) {
                dayItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };
        // 初始化选中月份和日期
        const initSelection = () => {
            if (props.value && props.value.length === 10) {
                tempValue.value = props.value;
                selectedMonth.value = props.value.substring(0, 7); // YYYY-MM
            }
            else if (monthList.value.length > 0) {
                // 如果没有选中值，默认选中第一个月份
                selectedMonth.value = monthList.value[0];
            }
            // 滚动到选中位置
            (0, vue_demi_1.nextTick)(() => {
                scrollToSelectedMonth();
                scrollToSelectedDay();
                // 如果选中了月份但没有选中日期，滚动到第一个可用日期
                if (selectedMonth.value && !tempValue.value) {
                    scrollToFirstAvailableDay();
                }
            });
        };
        // 处理确认
        const handleConfirm = () => {
            emit('confirm', tempValue.value);
            emit('update:visible', false);
        };
        // 处理取消
        const handleCancel = () => {
            // 恢复原始值
            tempValue.value = props.value;
            emit('cancel');
            emit('update:visible', false);
        };
        // 监听 visible 变化，初始化选择
        (0, vue_demi_1.watch)(() => props.visible, (newVal) => {
            if (newVal) {
                initSelection();
            }
            else {
                // 关闭时清理引用
                monthItemRefs.value.clear();
                dayItemRefs.value.clear();
            }
        }, { immediate: true });
        // 监听 value 变化
        (0, vue_demi_1.watch)(() => props.value, (newVal) => {
            tempValue.value = newVal;
            if (newVal && newVal.length === 10) {
                selectedMonth.value = newVal.substring(0, 7);
            }
        });
        // 监听 selectedMonth 变化，滚动到第一个可用日期
        (0, vue_demi_1.watch)(selectedMonth, () => {
            (0, vue_demi_1.nextTick)(() => {
                scrollToFirstAvailableDay();
            });
        });
        return {
            monthColumnRef,
            dayColumnRef,
            tempValue,
            selectedMonth,
            monthList,
            filteredDates,
            cancelText,
            confirmText,
            isEnglishLang,
            setMonthItemRef,
            setDayItemRef,
            isDateDisabled,
            handleMonthSelect,
            handleDaySelect,
            handleConfirm,
            handleCancel,
            formatMonthLabel: utils_1.formatMonthLabel,
            getDateLabel: utils_1.getDateLabel
        };
    }
});
//# sourceMappingURL=data:application/json;base64,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